import { config } from '@/forms/config';
import type {
  FormConditionalValidation,
  FormField,
  FormInitialValues,
  FormValidations,
} from '@/forms/typings';
import type { StagedFieldsGroupFormField } from '@/forms/typings';
import { registerGroupRestrictionValidations } from '@/forms/utils/createAtLeastOneChildWithValueYup';
import { generateValidations } from '@/forms/utils/generateValidations';

import type { FormFieldModuleContext } from '../registry/field-module.types';

const isHiddenField = (field: FormField) =>
  'hidden' in field && field.hidden === true;

export function buildStagedFieldsGroupInitialState(
  field: FormField,
  values: FormInitialValues,
  validations: FormValidations,
  conditions: FormConditionalValidation[] | undefined,
  ctx: FormFieldModuleContext,
): { initialValues: FormInitialValues; validationFields: FormValidations } {
  let initialValues: FormInitialValues = { ...values };
  let validationFields: FormValidations = { ...validations };

  const f = field as StagedFieldsGroupFormField;

  if (f.notApplicableField) {
    initialValues[f.notApplicableField.fieldId] = f.notApplicableField.value;
  }

  const shouldValidateFieldId = `${f.fieldId}${config.shouldValidateFieldSuffix}`;
  const validateFirstStageFieldId = `${f.fieldId}${config.validateFirstStageFieldSuffix}`;

  initialValues[shouldValidateFieldId] = f.hasAnyValue;
  initialValues[validateFirstStageFieldId] = f.hasAnyValue;

  initialValues[`${f.fieldId}${config.requiredFieldSuffix}`] = false;

  const schema = generateValidations(f, conditions);

  if (schema) {
    validationFields[`${f.fieldId}${config.requiredFieldSuffix}`] = schema;
  }

  const combinedConditions: FormConditionalValidation[] = [
    {
      type: 'should-validate',
      relatedFieldId: shouldValidateFieldId,
    },
  ];

  if (conditions) {
    combinedConditions.push(...conditions);
  }

  // The provider fills visible first-stage fields in the Generate modal, before the entry has a value.
  const firstStageConditions: FormConditionalValidation[] = [
    {
      type: 'should-validate',
      relatedFieldId: validateFirstStageFieldId,
    },
    ...(conditions ?? []),
  ];

  registerGroupRestrictionValidations(
    initialValues,
    validationFields,
    f.fieldId,
    [...f.firstStageFields, ...f.otherFields],
    f.restriction,
    combinedConditions,
  );

  for (const firstStageField of f.firstStageFields) {
    // Hidden ones are inferred by the server after Generate, so they wait for the entry.
    const detailValuesAndValidations = ctx.recurse(
      firstStageField,
      initialValues,
      validationFields,
      isHiddenField(firstStageField) ? combinedConditions : firstStageConditions,
    );

    initialValues = {
      ...initialValues,
      ...detailValuesAndValidations.initialValues,
    };

    validationFields = {
      ...validationFields,
      ...detailValuesAndValidations.validationFields,
    };
  }

  for (const otherField of f.otherFields) {
    const detailValuesAndValidations = ctx.recurse(
      otherField,
      initialValues,
      validationFields,
      combinedConditions,
    );

    initialValues = {
      ...initialValues,
      ...detailValuesAndValidations.initialValues,
    };
    validationFields = {
      ...validationFields,
      ...detailValuesAndValidations.validationFields,
    };
  }

  if (f.naDetails && f.notApplicableField) {
    const combinedNAConditions: FormConditionalValidation[] = [
      { type: 'not-applicable', relatedFieldId: f.notApplicableField.fieldId },
      ...(conditions ?? []),
    ];

    const naValuesAndValidations = ctx.recurse(
      f.naDetails,
      initialValues,
      validationFields,
      combinedNAConditions,
    );

    initialValues = {
      ...initialValues,
      ...naValuesAndValidations.initialValues,
    };

    validationFields = {
      ...validationFields,
      ...naValuesAndValidations.validationFields,
    };
  }

  return { initialValues, validationFields };
}
